import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Plus, Pencil, Trash2, GripVertical, ChevronDown, ChevronRight } from 'lucide-react';
import { Card } from '@/components/Card';
import { useApi } from '@/hooks/useApi';
import { post, patch, del } from '@/lib/api';

interface TemplateStep {
  id: string;
  title: string;
  description?: string;
  category: string;
  assignee_type: string;
  due_offset_days: number;
  required: boolean;
  order: number;
}

interface OnboardingTemplate {
  id: string;
  name: string;
  description?: string;
  type: string;
  department?: string;
  tasks: TemplateStep[];
  is_default: boolean;
}

const EMPTY_FORM = {
  name: '',
  description: '',
  template_type: 'onboarding_template' as const,
  department: '',
  duration_days: 30,
  is_default: false,
  tasks: [] as Omit<TemplateStep, 'id'>[],
};

const EMPTY_STEP = {
  title: '',
  description: '',
  category: 'documentation' as const,
  assignee_type: 'hr' as const,
  due_offset_days: 0,
  required: true,
  order: 0,
};

const CATEGORY_LABELS: Record<string, string> = {
  documentation: 'Paperwork', it_setup: 'IT Setup', orientation: 'Orientation',
  training: 'Training', equipment: 'Equipment', access: 'Access/Accounts',
  policy: 'Policy', custom: 'Other',
};

const ASSIGNEE_LABELS: Record<string, string> = {
  manager: 'Manager', it: 'IT', hr: 'HR', buddy: 'Buddy', new_hire: 'New Hire',
};

export function OnboardingTemplatesAdminPage() {
  const navigate = useNavigate();
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const { data, refetch } = useApi<{ templates: OnboardingTemplate[] }>('admin/onboarding-templates');

  const templates = data?.templates ?? [];

  function openCreate() {
    setForm({ ...EMPTY_FORM, tasks: [] });
    setEditingId(null);
    setShowForm(true);
  }

  function openEdit(t: OnboardingTemplate) {
    setForm({
      name: t.name,
      description: t.description ?? '',
      template_type: (t.type as typeof EMPTY_FORM.template_type) ?? 'onboarding_template',
      department: t.department ?? '',
      duration_days: 30,
      is_default: t.is_default,
      tasks: t.tasks.map(s => ({ title: s.title, description: s.description ?? '', category: s.category, assignee_type: s.assignee_type, due_offset_days: s.due_offset_days, required: s.required, order: s.order })),
    });
    setEditingId(t.id);
    setShowForm(true);
  }

  function addStep() {
    setForm(f => ({ ...f, tasks: [...f.tasks, { ...EMPTY_STEP, order: f.tasks.length }] }));
  }

  function updateStep(idx: number, field: string, value: unknown) {
    setForm(f => ({ ...f, tasks: f.tasks.map((s, i) => i === idx ? { ...s, [field]: value } : s) }));
  }

  function removeStep(idx: number) {
    setForm(f => ({ ...f, tasks: f.tasks.filter((_, i) => i !== idx).map((s, i) => ({ ...s, order: i })) }));
  }

  function moveStep(idx: number, dir: -1 | 1) {
    const tasks = [...form.tasks];
    const swap = idx + dir;
    if (swap < 0 || swap >= tasks.length) return;
    const tmp = tasks[idx]!;
    tasks[idx] = tasks[swap]!;
    tasks[swap] = tmp;
    setForm(f => ({ ...f, tasks: tasks.map((s, i) => ({ ...s, order: i })) }));
  }

  async function handleSave() {
    setSaving(true);
    try {
      const payload = { ...form, department: form.department || undefined, description: form.description || undefined };
      if (editingId) {
        await patch(`admin/onboarding-templates/${editingId}`, payload);
      } else {
        await post('admin/onboarding-templates', payload);
      }
      setShowForm(false);
      refetch();
    } finally {
      setSaving(false);
    }
  }

  async function handleDelete(id: string, name: string) {
    if (!confirm(`Delete template "${name}"?`)) return;
    await del(`admin/onboarding-templates/${id}`);
    refetch();
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-3">
        <button onClick={() => navigate('/admin')} className="text-muted-foreground hover:text-foreground transition-colors">
          <ArrowLeft className="w-5 h-5" />
        </button>
        <h2 className="text-lg font-semibold text-foreground">Onboarding Templates</h2>
        <button onClick={openCreate} className="ml-auto flex items-center gap-2 px-3 py-1.5 bg-amber-500 hover:bg-amber-600 text-black text-sm font-medium rounded-lg transition-colors">
          <Plus className="w-4 h-4" /> New Template
        </button>
      </div>

      {showForm && (
        <Card className="border border-amber-500/30">
          <h3 className="text-sm font-semibold text-foreground mb-4">{editingId ? 'Edit Template' : 'New Onboarding Template'}</h3>
          <div className="grid md:grid-cols-2 gap-4 mb-4">
            <div>
              <label className="text-xs text-muted-foreground block mb-1">Template Name *</label>
              <input value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} className="w-full bg-surface-2 border border-border rounded-lg px-3 py-2 text-sm text-foreground" placeholder="Engineering New Hire" />
            </div>
            <div>
              <label className="text-xs text-muted-foreground block mb-1">Type</label>
              <select value={form.template_type} onChange={e => setForm(f => ({ ...f, template_type: e.target.value as typeof form.template_type }))} className="w-full bg-surface-2 border border-border rounded-lg px-3 py-2 text-sm text-foreground">
                <option value="onboarding_template">Onboarding</option>
                <option value="offboarding_template">Offboarding</option>
              </select>
            </div>
            <div>
              <label className="text-xs text-muted-foreground block mb-1">Target Department (optional)</label>
              <input value={form.department} onChange={e => setForm(f => ({ ...f, department: e.target.value }))} className="w-full bg-surface-2 border border-border rounded-lg px-3 py-2 text-sm text-foreground" placeholder="Engineering" />
            </div>
            <div>
              <label className="text-xs text-muted-foreground block mb-1">Expected Duration (days)</label>
              <input type="number" value={form.duration_days} onChange={e => setForm(f => ({ ...f, duration_days: parseInt(e.target.value) }))} className="w-full bg-surface-2 border border-border rounded-lg px-3 py-2 text-sm text-foreground" />
            </div>
            <div className="md:col-span-2">
              <label className="text-xs text-muted-foreground block mb-1">Description</label>
              <textarea value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))} rows={2} className="w-full bg-surface-2 border border-border rounded-lg px-3 py-2 text-sm text-foreground resize-none" />
            </div>
            <div className="flex items-center">
              <label className="flex items-center gap-2 text-sm text-foreground cursor-pointer">
                <input type="checkbox" checked={form.is_default} onChange={e => setForm(f => ({ ...f, is_default: e.target.checked }))} className="rounded" />
                Default template
              </label>
            </div>
          </div>

          <div className="border-t border-border pt-4">
            <div className="flex items-center justify-between mb-3">
              <h4 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">Steps ({form.tasks.length})</h4>
              <button onClick={addStep} className="flex items-center gap-1 text-xs text-amber-400 hover:text-amber-300 transition-colors">
                <Plus className="w-3 h-3" /> Add Step
              </button>
            </div>
            <div className="space-y-2">
              {form.tasks.map((step, idx) => (
                <div key={idx} className="bg-surface-2 rounded-lg p-3 border border-border">
                  <div className="grid md:grid-cols-3 gap-2 mb-2">
                    <div className="md:col-span-2">
                      <input value={step.title} onChange={e => updateStep(idx, 'title', e.target.value)} className="w-full bg-surface-3 border border-border rounded px-2 py-1.5 text-sm text-foreground" placeholder="Step title *" />
                    </div>
                    <div className="flex gap-1">
                      <select value={step.category} onChange={e => updateStep(idx, 'category', e.target.value)} className="flex-1 bg-surface-3 border border-border rounded px-2 py-1.5 text-xs text-foreground">
                        {Object.entries(CATEGORY_LABELS).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                      </select>
                    </div>
                  </div>
                  <div className="grid md:grid-cols-4 gap-2 items-center">
                    <select value={step.assignee_type} onChange={e => updateStep(idx, 'assignee_type', e.target.value)} className="bg-surface-3 border border-border rounded px-2 py-1.5 text-xs text-foreground">
                      {Object.entries(ASSIGNEE_LABELS).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                    </select>
                    <div className="flex items-center gap-1">
                      <input type="number" value={step.due_offset_days} onChange={e => updateStep(idx, 'due_offset_days', parseInt(e.target.value))} className="w-16 bg-surface-3 border border-border rounded px-2 py-1.5 text-xs text-foreground" />
                      <span className="text-xs text-muted-foreground">days</span>
                    </div>
                    <label className="flex items-center gap-1 text-xs text-foreground cursor-pointer">
                      <input type="checkbox" checked={step.required} onChange={e => updateStep(idx, 'required', e.target.checked)} className="rounded" />
                      Required
                    </label>
                    <div className="flex items-center gap-1 justify-end">
                      <button onClick={() => moveStep(idx, -1)} disabled={idx === 0} className="text-muted-foreground hover:text-foreground disabled:opacity-30 transition-colors p-1">
                        <GripVertical className="w-3 h-3" />
                      </button>
                      <button onClick={() => removeStep(idx)} className="text-muted-foreground hover:text-red-400 transition-colors p-1">
                        <Trash2 className="w-3 h-3" />
                      </button>
                    </div>
                  </div>
                </div>
              ))}
              {form.tasks.length === 0 && <p className="text-xs text-muted-foreground text-center py-4">No steps yet. Add steps to build this template.</p>}
            </div>
          </div>

          <div className="flex gap-2 mt-4">
            <button onClick={handleSave} disabled={saving || !form.name} className="px-4 py-2 bg-amber-500 hover:bg-amber-600 disabled:opacity-50 text-black text-sm font-medium rounded-lg transition-colors">
              {saving ? 'Saving...' : editingId ? 'Update' : 'Create'}
            </button>
            <button onClick={() => setShowForm(false)} className="px-4 py-2 bg-surface-2 hover:bg-surface-3 text-foreground text-sm rounded-lg transition-colors">Cancel</button>
          </div>
        </Card>
      )}

      <div className="space-y-3">
        {templates.length === 0 && !showForm && (
          <div className="text-center py-12 text-muted-foreground text-sm">No templates yet. Create one to streamline onboarding.</div>
        )}
        {templates.map(t => (
          <Card key={t.id}>
            <div className="flex items-start justify-between">
              <div className="flex-1">
                <div className="flex items-center gap-2">
                  <button onClick={() => setExpandedId(expandedId === t.id ? null : t.id)} className="text-left flex items-center gap-2">
                    {expandedId === t.id ? <ChevronDown className="w-4 h-4 text-muted-foreground" /> : <ChevronRight className="w-4 h-4 text-muted-foreground" />}
                    <span className="text-sm font-medium text-foreground">{t.name}</span>
                  </button>
                  <span className="text-xs px-2 py-0.5 rounded-full bg-surface-3 text-muted-foreground">{t.type === 'onboarding_template' ? 'Onboarding' : 'Offboarding'}</span>
                  {t.is_default && <span className="text-xs px-2 py-0.5 rounded-full bg-amber-500/10 text-amber-400">Default</span>}
                </div>
                <div className="flex gap-3 mt-1 text-xs text-muted-foreground ml-6">
                  <span>{t.tasks.length} step{t.tasks.length !== 1 ? 's' : ''}</span>
                  {t.department && <span>Dept: {t.department}</span>}
                  {t.description && <span>{t.description}</span>}
                </div>
                {expandedId === t.id && t.tasks.length > 0 && (
                  <div className="mt-3 ml-6 space-y-1">
                    {t.tasks.sort((a, b) => a.order - b.order).map((step, i) => (
                      <div key={step.id} className="flex items-center gap-2 text-xs text-muted-foreground">
                        <span className="w-5 h-5 rounded-full bg-surface-3 flex items-center justify-center text-xs shrink-0">{i + 1}</span>
                        <span className="text-foreground">{step.title}</span>
                        <span className="text-muted-foreground">— {ASSIGNEE_LABELS[step.assignee_type] ?? step.assignee_type}</span>
                        <span className="text-muted-foreground">Day {step.due_offset_days}</span>
                        {step.required && <span className="text-amber-500/70">required</span>}
                      </div>
                    ))}
                  </div>
                )}
              </div>
              <div className="flex items-center gap-2">
                <button onClick={() => openEdit(t)} className="text-muted-foreground hover:text-foreground transition-colors"><Pencil className="w-4 h-4" /></button>
                <button onClick={() => handleDelete(t.id, t.name)} className="text-muted-foreground hover:text-red-400 transition-colors"><Trash2 className="w-4 h-4" /></button>
              </div>
            </div>
          </Card>
        ))}
      </div>
    </div>
  );
}
